import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import {
  AdminClientApi,
  ClientCouponInterface,
} from '../../Admin-ClientLibrary';
import { getApiUrl } from '../../config/api';
import { editCouponSchema, EditCouponFormData } from '../../schemas/coupon.schema';
import './CouponDetail.css';

const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

const CouponDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState<ClientCouponInterface | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [wasUpdated, setWasUpdated] = useState<boolean>(false);

  // React Hook Form for Edit Coupon
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<EditCouponFormData>({
    resolver: zodResolver(editCouponSchema),
    defaultValues: {
      name: '',
      is_active: true,
    },
  });

  const watchedIsActive = watch('is_active');

  useEffect(() => {
    if (id) {
      fetchCoupon(parseInt(id));
    }
  }, [id]);

  // Reset form when coupon data is loaded
  useEffect(() => {
    if (coupon) {
      reset({
        name: coupon.name,
        is_active: coupon.is_active,
      });
    }
  }, [coupon, reset]);

  const fetchCoupon = async (couponId: number): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response = await adminApi.getCoupon({ id: couponId });

      if (response.success && response.coupon) {
        setCoupon(response.coupon);
      } else {
        setError(response.message || 'Coupon not found');
      }
    } catch (err: any) {
      console.error('Error fetching coupon:', err);
      setError(err.message || 'Failed to fetch coupon');
    } finally {
      setLoading(false);
    }
  };

  const onSave = async (data: EditCouponFormData): Promise<void> => {
    if (!coupon) return;

    try {
      setSaving(true);

      const response = await adminApi.updateCoupon({
        id: coupon.id,
        name: data.name !== coupon.name ? data.name : undefined,
        is_active: data.is_active !== coupon.is_active ? data.is_active : undefined,
      });

      if (response.success && response.coupon) {
        setCoupon(response.coupon);
        setWasUpdated(true);
      } else {
        alert(response.message || 'Failed to update coupon');
      }
    } catch (err: any) {
      console.error('Error updating coupon:', err);
      alert(err.message || 'Failed to update coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!coupon) return;

    if (!window.confirm(`Are you sure you want to delete "${coupon.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await adminApi.deleteCoupon({ id: coupon.id });

      if (response.success) {
        navigate('/coupons', { state: { refresh: true } });
      } else {
        alert(response.message || 'Failed to delete coupon');
      }
    } catch (err: any) {
      console.error('Error deleting coupon:', err);
      alert(err.message || 'Failed to delete coupon');
    }
  };

  const formatDiscount = (coupon: ClientCouponInterface): string => {
    if (coupon.percent_off) {
      return `${coupon.percent_off}% OFF`;
    }
    if (coupon.amount_off) {
      return `$${(coupon.amount_off / 100).toFixed(2)} OFF`;
    }
    return '-';
  };

  const formatDuration = (coupon: ClientCouponInterface): string => {
    if (coupon.duration === 'once') return 'One-time use';
    if (coupon.duration === 'forever') return 'Forever';
    if (coupon.duration === 'repeating') {
      return `Repeating for ${coupon.duration_in_months} month${coupon.duration_in_months !== 1 ? 's' : ''}`;
    }
    return coupon.duration;
  };

  const formatDate = (date: Date | undefined): string => {
    if (!date) return 'No expiration';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleBack = (): void => {
    if (wasUpdated) {
      navigate('/coupons', { state: { refresh: true } });
    } else {
      navigate('/coupons');
    }
  };

  if (loading) {
    return (
      <div className="coupon-detail-page">
        <div className="loading">Loading coupon...</div>
      </div>
    );
  }

  if (error || !coupon) {
    return (
      <div className="coupon-detail-page">
        <button className="back-button" onClick={handleBack}>
          <ArrowLeft size={16} />
          Back to Coupons
        </button>
        <div className="error-message">
          <p>{error || 'Coupon not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="coupon-detail-page">
      <div className="detail-header">
        <button className="back-button" onClick={handleBack}>
          <ArrowLeft size={16} />
          Back to Coupons
        </button>

        <div className="header-content">
          <div className="coupon-title">
            <h1>{coupon.name}</h1>
            <span className="stripe-id">{coupon.stripe_coupon_id}</span>
          </div>
          <div className="header-actions">
            <button
              className="btn-primary"
              onClick={handleSubmit(onSave)}
              disabled={!isDirty || saving}
            >
              <Save size={16} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button className="btn-danger" onClick={handleDelete}>
              <Trash2 size={16} />
              Delete
            </button>
          </div>
        </div>
      </div>

      <div className="detail-content">
        <div className="detail-card">
          <h2>Discount Details</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Discount</span>
              <div className="discount-highlight">{formatDiscount(coupon)}</div>
            </div>
            <div className="info-item">
              <span className="info-label">Duration</span>
              <span className="duration-badge">{formatDuration(coupon)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Status</span>
              <span className={`status-badge ${coupon.is_active ? 'active' : 'inactive'}`}>
                {coupon.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            {coupon.currency && (
              <div className="info-item">
                <span className="info-label">Currency</span>
                <span className="info-value">{coupon.currency.toUpperCase()}</span>
              </div>
            )}
          </div>
        </div>

        <div className="detail-card">
          <h2>Usage Statistics</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Times Redeemed</span>
              <span className="info-value large">{coupon.times_redeemed}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Max Redemptions</span>
              <span className="info-value">
                {coupon.max_redemptions || 'Unlimited'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Valid Until</span>
              <span className="info-value">{formatDate(coupon.valid_until)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Created</span>
              <span className="info-value">{formatDate(coupon.createdAt)}</span>
            </div>
          </div>
        </div>

        <div className="detail-card">
          <h2>Edit Coupon</h2>
          <form className="edit-form" onSubmit={handleSubmit(onSave)}>
            <div className="form-group">
              <label>Coupon Name</label>
              <input
                type="text"
                {...register('name')}
                placeholder="Enter coupon name"
                className={errors.name ? 'input-error' : ''}
              />
              {errors.name && <span className="form-error">{errors.name.message}</span>}
              <span className="form-hint">
                This is the only editable field in Stripe for existing coupons.
              </span>
            </div>

            <div className="form-group">
              <label>Status</label>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  {...register('is_active')}
                />
                <span className="toggle-label">
                  {watchedIsActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <span className="form-hint">
                Deactivating a coupon will prevent it from being used but won't affect existing subscriptions.
              </span>
            </div>

            <div className="form-group">
              <label>Discount Type</label>
              <input
                type="text"
                value={coupon.percent_off ? 'Percentage' : 'Fixed Amount'}
                disabled
              />
              <span className="form-hint">Cannot be changed after creation.</span>
            </div>

            <div className="form-group">
              <label>Duration</label>
              <input type="text" value={formatDuration(coupon)} disabled />
              <span className="form-hint">Cannot be changed after creation.</span>
            </div>

            {isDirty && (
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    if (coupon) {
                      reset({
                        name: coupon.name,
                        is_active: coupon.is_active,
                      });
                    }
                  }}
                >
                  Discard Changes
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={saving}
                >
                  <Save size={16} />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default CouponDetail;

